import { z } from "zod";

export const DOCUMENT_NORMALIZER_VERSION = "anydoc-0.2.4";
export const DOCUMENT_NORMALIZER_MAX_INPUT_BYTES = 10 * 1024 * 1024;
export const DOCUMENT_NORMALIZER_MAX_MARKDOWN_CHARS = 500_000;
export const DOCUMENT_NORMALIZER_MAX_REQUEST_BYTES = 8 * 1024;
export const DOCUMENT_NORMALIZER_MAX_RESPONSE_BYTES = 700 * 1024;

export const normalizableContentTypes = [
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
] as const;

export const normalizableContentTypeSchema = z.enum(normalizableContentTypes);
export type NormalizableContentType = z.infer<typeof normalizableContentTypeSchema>;

// AnyDoc agrupa XLS binario y XLSX bajo su formato interno `xlsx`. Conservamos
// por separado el formato de origen para no perder esa distinción operativa.
export const documentUploadFormatSchema = z.enum(["pdf", "xls", "xlsx"]);
export type DocumentUploadFormat = z.infer<typeof documentUploadFormatSchema>;

export const normalizedDocumentFormatSchema = z.enum(["pdf", "xlsx"]);
export type NormalizedDocumentFormat = z.infer<typeof normalizedDocumentFormatSchema>;

export const documentNormalizationRequestSchema = z.object({
  tenantId: z.string().trim().min(1).max(120),
  documentId: z.string().uuid(),
  r2Key: z.string().min(1).max(1_024),
  fileName: z.string().trim().min(1).max(240),
  contentType: normalizableContentTypeSchema,
  sizeBytes: z.number().int().positive().max(DOCUMENT_NORMALIZER_MAX_INPUT_BYTES),
}).strict().superRefine((value, context) => {
  if (!value.r2Key.startsWith(`${value.tenantId}/`)) {
    context.addIssue({
      code: "custom",
      path: ["r2Key"],
      message: "La clave del documento no pertenece al tenant indicado.",
    });
  }
});

export type DocumentNormalizationRequest = z.infer<typeof documentNormalizationRequestSchema>;

export const documentNormalizationErrorCodeSchema = z.enum([
  "invalid_request",
  "not_found",
  "metadata_mismatch",
  "size_mismatch",
  "format_mismatch",
  "unsupported",
  "malformed",
  "encrypted",
  "resource_limit",
  "missing_part",
  "unsafe_archive",
  "timeout",
  "busy",
  "service_unavailable",
  "internal",
]);

export type DocumentNormalizationErrorCode = z.infer<typeof documentNormalizationErrorCodeSchema>;

const normalizationBaseSchema = z.object({
  provider: z.literal(DOCUMENT_NORMALIZER_VERSION),
  sourceFormat: documentUploadFormatSchema,
  format: normalizedDocumentFormatSchema,
  sourceSha256: z.string().regex(/^[0-9a-f]{64}$/),
});

export const documentNormalizationResultSchema = z.discriminatedUnion("status", [
  normalizationBaseSchema.extend({
    status: z.literal("normalized"),
    markdown: z.string().min(1).max(DOCUMENT_NORMALIZER_MAX_MARKDOWN_CHARS),
    markdownChars: z.number().int().positive().max(DOCUMENT_NORMALIZER_MAX_MARKDOWN_CHARS),
  }),
  normalizationBaseSchema.extend({
    status: z.literal("needs_ocr"),
    pages: z.array(z.number().int().positive()).max(1_000),
    pageCount: z.number().int().positive().max(10_000),
  }),
  z.object({
    status: z.literal("error"),
    provider: z.literal(DOCUMENT_NORMALIZER_VERSION),
    code: documentNormalizationErrorCodeSchema,
    retryable: z.boolean(),
  }),
]);

export type DocumentNormalizationResult = z.infer<typeof documentNormalizationResultSchema>;

export function formatForUpload(
  fileName: string,
  contentType: NormalizableContentType,
): DocumentUploadFormat | null {
  const lowerName = fileName.trim().toLowerCase();
  if (contentType === "application/pdf") return lowerName.endsWith(".pdf") ? "pdf" : null;
  if (contentType === "application/vnd.ms-excel") return lowerName.endsWith(".xls") ? "xls" : null;
  return lowerName.endsWith(".xlsx") ? "xlsx" : null;
}

export const documentNormalizationStatusSchema = z.enum([
  "pending",
  "processing",
  "normalized",
  "needs_ocr",
  "unsupported",
  "failed",
]);

export type DocumentNormalizationStatus = z.infer<typeof documentNormalizationStatusSchema>;

export const documentReviewFieldsSchema = z.object({
  registration: z.string().trim().min(1).max(16).optional(),
  vin: z.string().trim().min(1).max(32).optional(),
  make: z.string().trim().min(1).max(80).optional(),
  model: z.string().trim().min(1).max(120).optional(),
  firstRegistrationAt: z.string().date().optional(),
  powerHp: z.number().int().min(1).max(2_500).optional(),
}).strict();

export type DocumentReviewFields = z.infer<typeof documentReviewFieldsSchema>;

export const documentReviewRejectionReasonCodes = [
  "wrong_vehicle",
  "inaccurate_data",
  "unreadable",
  "duplicate",
  "other",
] as const;
export const documentReviewRejectionReasonCodeSchema = z.enum(documentReviewRejectionReasonCodes);
export type DocumentReviewRejectionReasonCode = z.infer<typeof documentReviewRejectionReasonCodeSchema>;

export const documentNormalizationRetryReasonCodes = [
  "manual_retry",
  "source_replaced",
  "temporary_failure",
  "other",
] as const;
export const documentNormalizationRetryReasonCodeSchema = z.enum(documentNormalizationRetryReasonCodes);
export type DocumentNormalizationRetryReasonCode = z.infer<typeof documentNormalizationRetryReasonCodeSchema>;

export const documentReviewSchema = z.discriminatedUnion("action", [
  z.object({
    action: z.literal("apply"),
    expectedVersion: z.number().int().positive(),
    baseVehicleUpdatedAt: z.string().min(1).max(40),
    fields: documentReviewFieldsSchema.refine((fields) => Object.keys(fields).length > 0, {
      message: "Selecciona al menos un dato para aplicarlo.",
    }),
  }).strict(),
  z.object({
    action: z.literal("reject"),
    expectedVersion: z.number().int().positive(),
    baseVehicleUpdatedAt: z.string().min(1).max(40),
    reasonCode: documentReviewRejectionReasonCodeSchema,
    note: z.string().trim().min(3).max(500).optional(),
  }).strict(),
]);

export type DocumentReviewInput = z.infer<typeof documentReviewSchema>;

export const documentNormalizationRetrySchema = z.object({
  expectedVersion: z.number().int().positive(),
  reasonCode: documentNormalizationRetryReasonCodeSchema,
  note: z.string().trim().min(3).max(500).optional(),
}).strict();

export type DocumentNormalizationRetryInput = z.infer<typeof documentNormalizationRetrySchema>;
